import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '../../../../../app/store/utils/baseQueryWithReauth';
import {
  SYSTEM_BILLING_BASE,
  unwrapApiData,
} from '../../../shared/infrastructure/api/unwrapApiData';
import type {
  WalletBlockInput,
  WalletCreditInput,
  WalletDebitInput,
  WalletUnblockInput,
  WalletDetail,
  WalletTransactionsParams,
  WalletTransactionsResponse,
  WalletsListParams,
  WalletsListResponse,
} from '../../domain/wallet.types';

export const billingWalletApi = createApi({
  reducerPath: 'billingWalletApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['WalletList', 'WalletDetail', 'WalletTransactions'],
  endpoints: build => ({
    getWalletsList: build.query<WalletsListResponse, WalletsListParams>({
      query: params => ({
        url: `${SYSTEM_BILLING_BASE}/wallets/list`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<WalletsListResponse>(response),
      providesTags: ['WalletList'],
    }),

    getWalletById: build.query<WalletDetail, number>({
      query: walletId => ({
        url: `${SYSTEM_BILLING_BASE}/wallets/${walletId}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapApiData<WalletDetail>(response),
      providesTags: (_result, _error, walletId) => [
        { type: 'WalletDetail', id: walletId },
      ],
    }),

    getWalletTransactions: build.query<
      WalletTransactionsResponse,
      { walletId: number; params?: WalletTransactionsParams }
    >({
      query: ({ walletId, params }) => ({
        url: `${SYSTEM_BILLING_BASE}/wallets/${walletId}/transactions`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: unknown) =>
        unwrapApiData<WalletTransactionsResponse>(response),
      providesTags: (_result, _error, { walletId }) => [
        { type: 'WalletTransactions', id: walletId },
      ],
    }),

    creditWallet: build.mutation<unknown, { walletId: number; body: WalletCreditInput }>({
      query: ({ walletId, body }) => ({
        url: `${SYSTEM_BILLING_BASE}/wallets/${walletId}/credit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { walletId }) => [
        'WalletList',
        { type: 'WalletDetail', id: walletId },
        { type: 'WalletTransactions', id: walletId },
      ],
    }),

    debitWallet: build.mutation<unknown, { walletId: number; body: WalletDebitInput }>({
      query: ({ walletId, body }) => ({
        url: `${SYSTEM_BILLING_BASE}/wallets/${walletId}/debit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { walletId }) => [
        'WalletList',
        { type: 'WalletDetail', id: walletId },
        { type: 'WalletTransactions', id: walletId },
      ],
    }),

    blockWallet: build.mutation<unknown, { walletId: number; body: WalletBlockInput }>({
      query: ({ walletId, body }) => ({
        url: `${SYSTEM_BILLING_BASE}/wallets/${walletId}/block`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { walletId }) => [
        'WalletList',
        { type: 'WalletDetail', id: walletId },
      ],
    }),

    unblockWallet: build.mutation<unknown, { walletId: number; body: WalletUnblockInput }>({
      query: ({ walletId, body }) => ({
        url: `${SYSTEM_BILLING_BASE}/wallets/${walletId}/unblock`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { walletId }) => [
        'WalletList',
        { type: 'WalletDetail', id: walletId },
      ],
    }),
  }),
});

export const {
  useGetWalletsListQuery,
  useGetWalletByIdQuery,
  useGetWalletTransactionsQuery,
  useCreditWalletMutation,
  useDebitWalletMutation,
  useBlockWalletMutation,
  useUnblockWalletMutation,
} = billingWalletApi;
